/**
 * Lifecycle trace helpers for chat turns (HIG-6457). The event shapes live in
 * `@shared/chat-telemetry/turn-events` because main records them on the
 * OpenTelemetry tracer; this module holds the engine-side builders. Pure TS,
 * no React, never throws.
 */
import type { Result } from 'neverthrow'
import type { TurnErrorIdentity, TurnTraceEvent } from '@shared/chat-telemetry/turn-events'
import type { ChatTurnError, TurnCompletion, TurnErrorContext } from './errors'

export type { StreamEndReason, TurnTraceEvent } from '@shared/chat-telemetry/turn-events'

/**
 * The error context plus the turn's identity: what every trace event and
 * error report from inside a turn carries. Engine entry points build one and
 * spread it into each event.
 */
export type TurnScope = TurnErrorContext & { readonly turnId: string }

/** One-line rendering of anything caught, for trace payloads. Never the raw object. */
export function describeCause(cause: unknown): string {
  if (cause instanceof Error) {
    return cause.message ? `${cause.name}: ${cause.message}` : cause.name
  }
  if (typeof cause === 'string') return cause
  if (cause === undefined || cause === null) return String(cause)
  try {
    return JSON.stringify(cause)
  } catch {
    return String(cause)
  }
}

/** Compact `tag[/kind][ code][ (message)]` label for a turn error. */
export function describeTurnErrorForTrace(error: ChatTurnError): string {
  switch (error._tag) {
    case 'BackendTurnError':
      return `${error._tag}/${error.kind}${error.errorCode ? ` ${error.errorCode}` : ''} (${error.message})`
    case 'SseConnectionError':
    case 'UnknownTurnError':
      return `${error._tag} (${describeCause(error.cause)})`
    case 'SseDecodeError':
      return `${error._tag} (${error.rawFrame.length} chars)`
    case 'ArkoseChallengeError':
      return `${error._tag}/${error.reason} (${error.message})`
    case 'AttachmentUploadError':
    case 'CreditsExhausted':
    case 'SubscriptionRequired':
    case 'CardRequired':
    case 'OnboardingRequired':
      return `${error._tag} (${error.message})`
    case 'StaleVerifyDropped':
      return `${error._tag}/${error.reason}`
  }
}

/** The searchable identity of a turn error: what to filter spans by. */
export function turnErrorIdentity(error: ChatTurnError): TurnErrorIdentity {
  return {
    errorTag: error._tag,
    errorKind: error._tag === 'BackendTurnError' ? error.kind : undefined,
    errorCode: (error._tag === 'BackendTurnError' && error.errorCode) || undefined,
    rayId: 'rayId' in error ? error.rayId : undefined,
  }
}

/** The `turn:settled` event for an engine entry point's final Result. */
export function settledTrace(
  scope: TurnScope,
  result: Result<TurnCompletion, ChatTurnError>,
  durationMs: number,
): TurnTraceEvent {
  if (result.isErr()) {
    return {
      kind: 'turn:settled',
      ...scope,
      outcome: 'failed',
      detail: describeTurnErrorForTrace(result.error),
      durationMs,
      ...turnErrorIdentity(result.error),
    }
  }
  const completion = result.value
  return {
    kind: 'turn:settled',
    ...scope,
    outcome: completion._tag,
    detail: completion._tag === 'interrupted' ? completion.reason : undefined,
    durationMs,
  }
}
